// The tenant logo is the brand shown inside the ERP itself — the sidebar and
// the login screen — `company_info.logo` — and is independent of the print logo
// (`print_logo`, on documents) and the favicon (the browser tab). `null` means "none
// uploaded", which every reader renders as the official Amer logo.
//
// Cached the same way as the favicon (see utils/favicon.js): the login screen
// renders before anyone is signed in, so a first paint that waits for the
// branding request would flash the Amer logo on every visit. The `storage`
// event carries a change made in one window to every other open one.
export const TENANT_LOGO_STORAGE_KEY = "tenant-logo-url";

const readCached = () => {
  try {
    return localStorage.getItem(TENANT_LOGO_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

let current = readCached();
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

export const getTenantLogo = () => current;

/**
 * Records the tenant's saved logo URL and repaints every reader. Pass only the
 * backend's own `company_info.logo_url` (or a falsy value to clear it) — never a
 * transient `URL.createObjectURL` preview, which dies with this tab and would
 * poison every other window's cache.
 */
export function setTenantLogo(url) {
  const next = url || null;
  if (next === current) return;
  current = next;
  try {
    if (next) {
      localStorage.setItem(TENANT_LOGO_STORAGE_KEY, next);
    } else {
      localStorage.removeItem(TENANT_LOGO_STORAGE_KEY);
    }
  } catch {
    // Private-mode/quota failures only cost the cache; this tab still repaints.
  }
  notify();
}

export function subscribeTenantLogo(listener) {
  const onStorage = (event) => {
    if (event.key !== TENANT_LOGO_STORAGE_KEY) return;
    current = event.newValue || null;
    listener();
  };
  listeners.add(listener);
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}
